import React from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import xor from 'lodash/xor';
import Router, {
  useRouter,
  getQueryString,
  removeQueryParams,
  getCurrentPath,
} from '@/Framework/Router';
import Tooltip from '@dealroadshow/uikit/core/components/Tooltip';
import { usePageFiltersContext } from '@/finsight/application/PageFilters/PageFiltersContext';

import styles from './togglingFilters.scss';

interface IFilterSet {
  key: string,
  items: Array<any>,
  isVisible?: boolean,
}

interface IProps {
  onToggle?: (searchQuery: string, filterName: string, abbreviation: string) => void,
  filterSet?: Array<IFilterSet>,
  isDisabled?: boolean,
}

/**
 * TODO: come up / merge with ui/finsight/common/components/TogglingFilters
 */
const TogglingFilters = ({
  onToggle = noop,
  filterSet,
  isDisabled = false,
}: IProps) => {
  const {
    productFilters,
    regionFilters,
  } = usePageFiltersContext();
  filterSet = isEmpty(filterSet) ? [
    {
      key: 'products',
      items: productFilters,
    },
    {
      key: 'regions',
      items: regionFilters,
    },
  ] : filterSet;

  const {
    query,
    asPath,
  }: { query: object, asPath: string } = useRouter();
  const currentFilters = removeQueryParams(
    query,
    ['sector', 'industry', 'leagueType', 'viewId', 'companyName', 'routeType', 'tab'],
  );

  const applyFilter = (filterName: string, abbreviation: string) => {
    if (isDisabled) {
      return;
    }
    const selectedFilterList: Array<string> = xor(currentFilters[filterName].split(','), [abbreviation]);

    if (isEmpty(selectedFilterList)) {
      return;
    }

    const newFilters = {
      ...currentFilters,
      [filterName]: selectedFilterList.join(','),
    };

    const newSearchQuery = getQueryString(newFilters);

    Router.push(`${ getCurrentPath(asPath) }?${ newSearchQuery }`);

    onToggle(newSearchQuery, filterName, abbreviation);
  };

  return (
    <>
      {
        filterSet.map((filterData) => {
          if (filterData.isVisible !== undefined && !filterData.isVisible) {
            return null;
          }

          return (
            <div key={ filterData.key } className={ styles.filtersWrapper }>
              {
                filterData.items.map((filter) => (
                  <Tooltip
                    key={ filter.id }
                    content={ filter.tooltip }
                  >
                    <button
                      type="button"
                      onClick={ () => applyFilter(filterData.key, filter.abbreviation) }
                      className={ cn(styles.filter, {
                        [styles.isActive]: filter.isActive,
                        [styles.isDisabled]: isDisabled,
                      }) }
                      data-test="togglingFilterApplyButton"
                    >
                      { filter.abbreviation }
                    </button>
                  </Tooltip>
                ))
              }
            </div>
          );
        })
      }
    </>
  );
};

export default TogglingFilters;
